import { supabase } from "@/integrations/supabase/client";
import { Sorteio, Modalidade, analisar, ordenar } from "./analiseEngine";

export type PalpitesSalvos = {
  milhares?: string[];
  centenas?: string[];
  dezenas?: string[];
  grupos?: number[];
  duques_dezenas?: string[];
  duques_grupos?: string[];
  ternos_dezenas?: string[];
  ternos_grupos?: string[];
};

export type OcorrenciaHistorica = {
  data: string;
  turno: string;
  posicoes: number[];
};

export type HistoricoPalpiteItem = {
  valor: string;
  total: number;
  ocorrencias: OcorrenciaHistorica[];
  justificativa: string;
};

export type HistoricoPremiacoes = {
  gerado_em: string;
  modalidades: Partial<Record<"milhares" | "centenas" | "duques_dezenas" | "ternos_dezenas", {
    janela_dias: number;
    itens: HistoricoPalpiteItem[];
  }>>;
};

export const TURNO_ORDEM = ["PPT", "PTM", "PT", "PTV", "PTN", "COR"];

export const rotuloFonte = (source: string) =>
  source === "rio" ? "Paratodos Rio" : source === "federal" ? "Federal" : source;

export const rotuloPremios = (posicoes: unknown) => {
  if (!Array.isArray(posicoes) || posicoes.length === 0) return "Todos os prêmios disponíveis";
  const premios = [...new Set(posicoes.map(Number).filter(Number.isFinite))].sort((a, b) => a - b);
  if (premios.length === 0) return "Todos os prêmios disponíveis";
  if (premios.length === 1) return `${premios[0]}º prêmio`;
  const sequenciais = premios.every((premio, index) => premio === premios[0] + index);
  if (sequenciais) return `${premios[0]}º ao ${premios[premios.length - 1]}º prêmio`;
  return `${premios.map((premio) => `${premio}º`).join(", ")} prêmios`;
};

export const rotuloValidade = (source: string, janela?: number) => {
  const total = janela || (source === "rio" ? 12 : 1);
  return source === "rio"
    ? `Próximos ${total} sorteios completos após salvar`
    : "Primeira extração Federal após salvar";
};

export const rotuloDiasSemana = (dias: unknown) => {
  if (!Array.isArray(dias) || dias.length === 0) return "Todos os dias";
  const nomes = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const validos = [...new Set(dias.map(Number).filter((dia) => Number.isInteger(dia) && dia >= 0 && dia <= 6))];
  return validos.length ? validos.map((dia) => nomes[dia]).join(", ") : "Todos os dias";
};

export const rotuloModalidades = (modalidades: unknown) => {
  const nomes: Record<string, string> = {
    grupo: "Grupos",
    dezena: "Dezenas",
    centena: "Centenas",
    milhar: "Milhares",
    duque_dezena: "Duques de dezenas",
    duque_grupo: "Duques de grupos",
    terno_dezena: "Ternos de dezenas",
    terno_grupo: "Ternos de grupos",
  };
  if (!Array.isArray(modalidades) || modalidades.length === 0) return "Não registrado na geração";
  return modalidades.map(String).map((item) => nomes[item] || item).join(", ");
};

export const rotuloPeriodoHistorico = (filtros: Record<string, unknown> | null | undefined) => {
  const modo = filtros?.modo_recorte;
  const valor = String(filtros?.recorte_valor ?? "");

  if (!valor) return "Período histórico não registrado na geração";
  if (modo === "sorteios") {
    return valor === "9999" ? "Todos os sorteios disponíveis" : `Últimos ${valor} sorteios`;
  }
  if (modo !== "periodo") return "Período histórico não registrado na geração";
  if (valor === "9999") return "Todo o período disponível";
  if (valor === "365") return "Último 1 ano";
  if (valor === "180") return "Últimos 6 meses";
  return `Últimos ${valor} dias`;
};

const top = (sorteios: Sorteio[], modalidade: Modalidade, quantidade: number) =>
  ordenar(analisar(sorteios, modalidade).itens, "alta")
    .sort((a, b) => {
      const scoreA = a.freq * Math.max(a.tendencia, 0.5) + Math.min(a.atraso, 5) * 0.15;
      const scoreB = b.freq * Math.max(b.tendencia, 0.5) + Math.min(b.atraso, 5) * 0.15;
      return scoreB - scoreA || b.freq - a.freq;
    })
    .slice(0, quantidade)
    .map((item) => item.key);

export const gerarPalpitesAvancados = (sorteios: Sorteio[]): PalpitesSalvos => ({
  milhares: top(sorteios, "milhar", 6),
  centenas: top(sorteios, "centena", 6),
  duques_dezenas: top(sorteios, "duque_dezena", 6),
  duques_grupos: top(sorteios, "duque_grupo", 3),
  ternos_dezenas: top(sorteios, "terno_dezena", 3),
  ternos_grupos: top(sorteios, "terno_grupo", 3),
  grupos: top(sorteios, "grupo", 4).map(Number),
});

const inicioJanela = (dataFinal: string, dias: number) => {
  const data = new Date(`${dataFinal}T12:00:00Z`);
  data.setUTCDate(data.getUTCDate() - dias);
  return data.toISOString().slice(0, 10);
};

const normalizarCombinacao = (valor: string, preencherDezena = false) =>
  (valor.match(/\d+/g) || [])
    .map(Number)
    .map((numero) => preencherDezena ? String(numero).padStart(2, "0") : String(numero))
    .sort((a, b) => Number(a) - Number(b))
    .join("-");

const justificar = (sorteios: Sorteio[], modalidade: Modalidade, valor: string) => {
  const item = analisar(sorteios, modalidade).itens.find((rank) => rank.key === valor);
  if (!item) return "Selecionado pelo modelo pelo conjunto de frequência, tendência recente e atraso no recorte configurado.";
  const tendencia = item.tendencia >= 1.15
    ? "tendência recente acima da própria média"
    : item.tendencia >= 0.85
      ? "frequência recente estável"
      : "potencial de retorno após menor presença recente";
  return `Selecionado pelo modelo por reunir ${item.freq} ocorrência(s), ${tendencia} e atraso atual de ${item.atraso} corrida(s) no recorte analisado.`;
};

export const gerarHistoricoPremiacoes = (
  palpites: PalpitesSalvos,
  sorteios: Sorteio[],
  dataFinal?: string,
): HistoricoPremiacoes => {
  const datasOrdenadas = sorteios.map((sorteio) => sorteio.data).sort();
  const referencia = dataFinal || datasOrdenadas[datasOrdenadas.length - 1] || new Date().toISOString().slice(0, 10);
  const modalidades: HistoricoPremiacoes["modalidades"] = {};
  const configuracoes: Array<{
    chave: "milhares" | "centenas" | "duques_dezenas" | "ternos_dezenas";
    modalidade: Modalidade;
    dias: number;
    tamanho?: number;
  }> = [
    { chave: "milhares", modalidade: "milhar", dias: 21 },
    { chave: "centenas", modalidade: "centena", dias: 21 },
    { chave: "duques_dezenas", modalidade: "duque_dezena", dias: 21, tamanho: 2 },
    { chave: "ternos_dezenas", modalidade: "terno_dezena", dias: 21, tamanho: 3 },
  ];

  for (const config of configuracoes) {
    const valores = palpites[config.chave];
    if (!Array.isArray(valores) || valores.length === 0) continue;
    const inicio = inicioJanela(referencia, config.dias);
    const recorte = sorteios.filter((sorteio) => sorteio.data >= inicio && sorteio.data <= referencia);
    const itens = valores.map((valorOriginal) => {
      const valor = String(valorOriginal);
      const ocorrencias: OcorrenciaHistorica[] = [];
      for (const sorteio of recorte) {
        if (config.tamanho) {
          const desejadas = new Set(normalizarCombinacao(valor, true).split("-"));
          const linhas = sorteio.rows.filter((row) => desejadas.has(String(row.milhar).slice(-2)));
          const encontradas = new Set(linhas.map((row) => String(row.milhar).slice(-2)));
          if (encontradas.size === config.tamanho) {
            ocorrencias.push({
              data: sorteio.data,
              turno: sorteio.turno,
              posicoes: [...new Set(linhas.map((row) => Number(row.posicao)))].sort((a, b) => a - b),
            });
          }
        } else {
          const linhas = sorteio.rows.filter((row) =>
            config.chave === "milhares"
              ? String(row.milhar).padStart(4, "0") === valor.padStart(4, "0")
              : String(row.milhar).slice(-3).padStart(3, "0") === valor.padStart(3, "0")
          );
          linhas.forEach((row) => ocorrencias.push({ data: sorteio.data, turno: sorteio.turno, posicoes: [Number(row.posicao)] }));
        }
      }
      return {
        valor,
        total: ocorrencias.length,
        ocorrencias,
        justificativa: justificar(sorteios, config.modalidade, config.tamanho ? normalizarCombinacao(valor, true) : valor),
      };
    });
    modalidades[config.chave] = { janela_dias: config.dias, itens };
  }

  return { gerado_em: new Date().toISOString(), modalidades };
};

export const getLatestDrawMarker = async (source: "rio" | "federal") => {
  if (source === "federal") {
    const { data } = await supabase
      .from("deu_no_poste_federal")
      .select("data")
      .order("data", { ascending: false })
      .limit(1);
    return data?.[0] ? { data: data[0].data, turno: "FED" } : null;
  }

  const { data } = await supabase
    .from("deu_no_poste_resultados")
    .select("data, turno")
    .order("data", { ascending: false })
    .limit(60);
  if (!data?.length) return null;
  const latestDate = data[0].data;
  const latest = data
    .filter((row) => row.data === latestDate)
    .sort((a, b) => TURNO_ORDEM.indexOf(b.turno) - TURNO_ORDEM.indexOf(a.turno))[0];
  return latest ? { data: latest.data, turno: latest.turno } : null;
};

export const salvarPalpites = async ({
  palpites,
  source,
  origem,
  filtros,
}: {
  palpites: PalpitesSalvos;
  source: "rio" | "federal";
  origem: "ia" | "estatisticas" | "avancada";
  filtros?: Record<string, unknown>;
}) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("É necessário estar logado para salvar jogos");
  const marcador = await getLatestDrawMarker(source);
  const janela = source === "rio" ? 12 : 1;
  const { error } = await supabase.from("palpites_ia_gerados" as any).insert({
    user_id: user.id,
    source,
    palpites: palpites as any,
    filtros_usados: {
      ...filtros,
      origem,
      marcador_geracao: marcador,
      janela_sorteios: janela,
    } as any,
    data_referencia: marcador?.data || new Date().toISOString().split("T")[0],
  });
  if (error) throw error;
  window.dispatchEvent(new CustomEvent("palpites-ia:refresh"));
};
